<template>
    <div class="my-4"> 
        <div class="bgTR px-4 px-lg-5 py-4 py-lg-5">
            <div class="schritt-zaehler-position">
                <SchritteZaehler class="w-100"></SchritteZaehler>
            </div>
            <div class="w-100 d-flex flex-column flex-md-row align-items-start gap-3 mb-3 mt-3 justify-self-start">
              
                <h3>Übersicht der zu versichernden Personen</h3>
              
              
                <RdnummerOk 
                              v-if="shouldShowRdnummerInfo" 
                              :rdnummer="store.aok" 
                              class="ms-auto"
                            />
              
            </div>
            
           

            <div class="boxTR px-4 px-lg-5 py-3 py-lg-4 w-100 font-base-links">
                <div class="my-4">
                    <!-- kombi infokachel schritt 3 -->
                    <KombiInfokachelSchritt3 
                        :items="store.items"
                        :gesamt="store.gesamt"
                        :vndaten="store.vndaten"
                        :versicherungsbeginn="store.versicherungsbeginn"
                    ></KombiInfokachelSchritt3>
                </div>
            </div>

            <div v-if="store.hatStationaer()" class="mt-4 w-100">
                <KombirechnerVergangenheitsklauselInfo></KombirechnerVergangenheitsklauselInfo>
            </div>

            <div class="mt-4 mb-3 w-100">
                <div class="d-flex align-items-start align-items-md-end">
                <KleinerFehlerIcon class="me-3"></KleinerFehlerIcon>
                <b>Bitte laden Sie zunächst dieses PDF-Dokument herunter, damit Sie fortfahren können:</b>                    
                </div>

                <ul class="ms-3 ms-md-5 mt-3 listeimp">
                    <li><b>Wesentliche Informationen</b> (beinhaltet die Allgemeinen Versicherungsbedingungen) mit den</li>
                    <li v-if="mainStore.kanal === 'vigoselect'"><b>Vertragsgrundlagen und Erklärungen</b> des Antragsstellers und der zu versichernden Person(en), inkl. Einwilligungserklärung zur Nutzung von Sozialdaten der AOK Rheinland/Hamburg sowie Hinweise zum Widerrufsrecht</li>
                    <li v-else><b>Vertragsgrundlagen und Erklärungen</b> des Antragsstellers und der zu versichernden Person(en) sowie den Hinweisen zum Widerrufsrecht</li>
                    <li v-if="mainStore.aokEnabled && store.aok > 0"><b>Vermittlerinformation</b> zur AOK Rheinland/Hamburg</li>
                </ul>
            </div>

            <div class="mt-4 w-100">
                <KombirechnerPdfDownloadInfo></KombirechnerPdfDownloadInfo>
            </div>
            
            <!-- Fehleranzeige bei PDF-Download-Fehler -->
            <FehleranzeigeKlein 
              v-if="dokStore.errorMessage" 
              :message="dokStore.errorMessage" 
              class="mt-3 mb-3"
            />
            
            <div class="mt-4 d-flex justify-content-start w-100">
                <VertragsdokumenteHerunterladenButton 
                  :disabled="downloadDisabled"
                  :isLoading="dokStore.isDownloading"
                  @click="handleDownload"
                ></VertragsdokumenteHerunterladenButton>
            </div>
        </div>
    </div>
    <!-- Navigation Buttons außerhalb bgTR -->
    <div class="d-flex justify-content-end gap-3 mt-4">
      <ZurueckButton @click="handleZurueck" :disabled="zurueckDisabled" />
      <WeiterButton @click="handleWeiter" :disabled="weiterDisabled" />
    </div>
    
    <!-- Hinweis wenn Download läuft -->
    <p v-if="dokStore.isDownloading" class="text-end text-muted mt-2 font-sm-links">
      Bitte warten Sie, während die Vertragsdokumente generiert werden...
    </p>
    <!-- Hinweis wenn Download noch nicht erfolgt -->
    <p v-else-if="!downloadDisabled" class="text-end text-muted mt-2 font-sm-links">
      Bitte laden Sie zuerst die Vertragsdokumente herunter, um fortzufahren.
    </p>


</template>

<script setup>
import { computed } from 'vue';
import { useRechnerStore } from '@/stores/rechner';
import { useMainStore } from '@/stores/useMainStore.js';
import { useDokStore } from '@/stores/dok.js';
import { useAntragStore } from '@/stores/antrag.js';
import { generateAndDownloadPDF } from '@/api/DokService.js';
import SchritteZaehler from '@/components/general/schritte_zaehler.vue';
import KombirechnerVergangenheitsklauselInfo from '@/components/kombi/kombi_vergangenheitsklausel_info.vue';
import KombirechnerPdfDownloadInfo from '@/components/kombi/kombi_pdf_download_info.vue';
import KleinerFehlerIcon from '@/components/icons/kleiner_fehler_icon.vue';
import VertragsdokumenteHerunterladenButton from '@/components/vertragsdokumente_herunterladen_button.vue';
import KombiInfokachelSchritt3 from '@/components/kombi/kombi_infokachel_schritt3.vue';
import ZurueckButton from '@/components/general/zurueck_button.vue';
import WeiterButton from '@/components/general/weiter_button.vue';
import RdnummerOk from '@/components/general/rdnummer_ok.vue';
import FehleranzeigeKlein from '@/components/general/fehleranzeige_klein.vue';

const store = useRechnerStore();
const mainStore = useMainStore();
const dokStore = useDokStore();
const antragStore = useAntragStore();

// Prüft ob RD-Nummerninfo angezeigt werden soll
const shouldShowRdnummerInfo = computed(() => {
  // Nur anzeigen wenn Feature enabled UND RD-Nummer vorhanden (nicht 0)
  return mainStore.aokEnabled && store.aok !== 0
});

// Download-Status
const downloadDisabled = computed(() => {
  return store.hasDownload || dokStore.hasDownload;
});

// Zurück-Button Status: Disabled während Download und nach erfolgreichem Download
const zurueckDisabled = computed(() => {
  return dokStore.isDownloading || downloadDisabled.value;
});

// Weiter-Button Status: Disabled während Download oder wenn Download noch nicht erfolgt
const weiterDisabled = computed(() => {
  return dokStore.isDownloading || !downloadDisabled.value;
});

// PDF-Download Handler
const handleDownload = async () => {
  console.log('[kombi_schritt3] handleDownload gestartet');
  
  // Verhindere mehrfache Klicks
  if (dokStore.isDownloading) {
    return;
  }
  
  // Fehler zurücksetzen
  dokStore.resetError();
  
  // 1. Tarife sammeln
  const tarife = [];
  if (store.hatZVS()) tarife.push('ZVS');
  if (store.hatZVP()) tarife.push('ZVP');
  if (store.hatZX2()) tarife.push('ZX2');
  if (store.hatS1()) tarife.push('S1');
  if (store.hatS2()) tarife.push('S2');
  
  console.log('[kombi_schritt3] Ausgewählte Tarife:', tarife);
  
  // 2. DokStore befüllen
  dokStore.kanal = mainStore.kanal;
  dokStore.vnVorname = store.vndaten.vorname;
  dokStore.vnNachname = store.vndaten.nachname;
  dokStore.tarife = tarife;
  dokStore.aokMitarbeiter = store.aok > 0;
  
  // 3. Loading setzen
  dokStore.isDownloading = true;
  
  try {
    // 4. API Call mit AntragId
    if (!antragStore.hasValidAntragId) {
      throw new Error('Keine gültige AntragId vorhanden');
    }
    
    const result = await generateAndDownloadPDF(antragStore.antragId, dokStore);
    
    if (result.success) {
      // Erfolg: Download-Status setzen
      dokStore.hasDownload = true;
      store.hasDownload = true;
      console.log('[kombi_schritt3] PDF erfolgreich heruntergeladen');
    } else {
      // Fehler anzeigen
      dokStore.errorMessage = result.error || 'Fehler beim Generieren der Vertragsdokumente. Bitte versuchen Sie es erneut.';
      console.error('[kombi_schritt3] Fehler beim PDF-Download:', result.error);
    }
  } catch (error) {
    // Exception abfangen
    dokStore.errorMessage = 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
    console.error('[kombi_schritt3] Exception beim PDF-Download:', error);
  } finally {
    dokStore.isDownloading = false;
  }
};

// Navigation: Zurück zu Schritt 2 (nur wenn nicht disabled)
const handleZurueck = () => {
  if (!zurueckDisabled.value) {
    store.stepStatus = 2;
  }
};

// Navigation: Weiter zu Schritt 4 (nur wenn Download erfolgt)
const handleWeiter = () => {
  if (!weiterDisabled.value && downloadDisabled.value) {
    store.stepStatus = 4;
  } else if (!dokStore.isDownloading) {
    // Shake-Animation für Button wenn Download noch nicht erfolgt (aber nicht während Download)
    const button = document.querySelector('[data-shake]');
    if (button) {
      button.classList.add('shake');
      setTimeout(() => button.classList.remove('shake'), 500);
    }
  }
};
</script>

<style scoped>
    .listeimp {
      list-style-type: disc !important;
    }

    .introclaim {
        margin-bottom: 1.5rem;
    }
    .platziert {
        position: absolute;
        right:0;
        top:0;
        transform: translate(-20%,-50%);
    }


    .schritt-zaehler-position {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90vw;
    }

    @media (min-width: 768px) {
    .schritt-zaehler-position {
        width: 80vw;
    }
    }

    @media (min-width: 992px) {
    .schritt-zaehler-position {
        width: 70vw;
    }
    }

    @media (min-width: 1200px) {
    .schritt-zaehler-position {
        width: 60vw;
    }
    }

    h3 {
        color: #000;
        font-family: "Source Sans Pro";
        font-size: 22px;
        font-style: normal;
        font-weight: 700;
        line-height: 160%;
        text-align: left;
    }
</style>